import { useState } from "react";
import cx from "classnames";
import { faSliders } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import { toggleValue } from "common/utils/sets";
import { isHit } from "common/utils/strings";

import Accordion from "client/common/components/interactive/Accordion";
import CloseButton from "client/common/components/buttons/CloseButton";
import TextButton from "client/common/components/buttons/TextButton";
import Checkbox from "client/common/components/inputs/Checkbox";

import {
  PANEL_COLLAPSED_OPTION_LIMIT,
  PANEL_OPTION_SEARCH_THRESHOLD,
} from "../constants/filterDefinitions";
import { FilterValue, FilterDefinition, SearchFilterOption } from "../types";
import { filtersWithOptions, collapsedOptions, nestByParentCategory } from "../utils";
import { FilterPanelState } from "../hooks";
import AppliedFilters from "./AppliedFilters";
import AppliedFilterTags from "./AppliedFilterTags";

import styles from "../styles/FilterPanel.module.less";

type FilterOptionListProps = {
  /** Names the dimension being searched, e.g. "Find a manufacturer". */
  placeholder: string;
  options: SearchFilterOption<FilterValue>[];
  selected: FilterValue[];
  onChange: (next: FilterValue[]) => void;
};

/** The span is load-bearing: Checkbox's label is a flex container and would drop the whitespace. */
const highlightOptionMatch = ({ label, query }: { label: string; query: string }) => {
  const matchStart = query ? label.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) : -1;
  if (matchStart < 0) {
    return label;
  }
  return (
    <span>
      {label.slice(0, matchStart)}
      <mark className={styles.highlight}>{label.slice(matchStart, matchStart + query.length)}</mark>
      {label.slice(matchStart + query.length)}
    </span>
  );
};

const FilterOptionList = ({ placeholder, options, selected, onChange }: FilterOptionListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const selectedValues = new Set(selected);

  const isSearchable = options.length > PANEL_OPTION_SEARCH_THRESHOLD;
  const matchingOptions = query ? options.filter((option) => isHit(query, option.label)) : options;

  // A search is already a narrowing, so collapsing its results would hide the thing being looked
  // for behind a second click.
  const visibleOptions =
    isExpanded || query
      ? matchingOptions
      : collapsedOptions({
          options: matchingOptions,
          selected,
          limit: PANEL_COLLAPSED_OPTION_LIMIT,
        });

  return (
    <div className={styles.group}>
      {isSearchable && (
        <div className={styles.optionSearchRow}>
          <input
            type="text"
            className={styles.optionSearch}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={placeholder}
            aria-label={placeholder}
          />
          {!!query && (
            <CloseButton
              type="button"
              className={styles.optionSearchClear}
              aria-label="Clear search"
              onClick={() => setQuery("")}
            />
          )}
        </div>
      )}
      {!visibleOptions.length && <div className={styles.noMatches}>No matches</div>}
      {nestByParentCategory(visibleOptions).map(({ option, depth }) => (
        <div
          key={String(option.value)}
          className={cx(styles.option, depth > 0 && styles.nestedOption)}
        >
          <Checkbox
            value={selectedValues.has(option.value)}
            onChange={() => onChange([...toggleValue(selected, option.value)])}
            labelContent={highlightOptionMatch({ label: option.label, query })}
            styles={{ container: styles.checkbox, label: styles.optionLabel }}
          />
          <span className={styles.count}>{option.count}</span>
        </div>
      ))}
      {!query && visibleOptions.length < options.length && (
        <TextButton type="button" className={styles.showAll} onClick={() => setIsExpanded(true)}>
          Show all {options.length}
        </TextButton>
      )}
      {isExpanded && (
        <TextButton type="button" className={styles.showAll} onClick={() => setIsExpanded(false)}>
          Show less
        </TextButton>
      )}
    </div>
  );
};

type FilterPanelProps<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  /** The options each filter offers, keyed by filter, each carrying the count it would yield. */
  filterOptions: TFilterOptions;
  filters: TSearch;
  onChange: (next: Partial<TSearch>) => void;
  hasSelectedFilters: boolean;
  /** Owned by the page, because the chip row above the grid offers the same action. */
  onClearAll: () => void;
  /** Off in the modal, which supplies its own title and reset action. */
  showHeader?: boolean;
};

const FilterPanel = <TSearch, TFilterOptions>({
  definitions,
  filterOptions,
  filters,
  onChange,
  hasSelectedFilters,
  onClearAll,
  showHeader = true,
}: FilterPanelProps<TSearch, TFilterOptions>) => {
  const renderableFilters = filtersWithOptions({ definitions, filterOptions });

  if (!renderableFilters.length) {
    return null;
  }

  const accordionItems = renderableFilters.map(({ definition, options }) => {
    const selected = definition.selectedFrom(filters);
    return {
      key: definition.key,
      label: (
        <span className={styles.groupLabel}>
          {definition.label}
          {!!selected.length && <span className={styles.groupCount}>({selected.length})</span>}
        </span>
      ),
      content: (
        // A new option set needs a fresh FilterOptionList: the search query is local state,
        // so reusing the instance leaves the old query filtering the new options to "No
        // matches". Selecting an option moves counts but not values, so the key holds.
        <FilterOptionList
          key={options.map((option) => option.value).join(",")}
          placeholder={`Find a ${definition.singularLabel.toLocaleLowerCase()}`}
          options={options}
          selected={selected}
          onChange={(next) => onChange(definition.toUpdate(next))}
        />
      ),
    };
  });

  return (
    <div className={styles.panel}>
      {showHeader && (
        <div className={styles.header}>
          <h2 className={styles.title}>
            <Icon className={styles.titleIcon} icon={faSliders} />
            Filters
          </h2>
          <TextButton
            type="button"
            className={styles.clearAll}
            onClick={onClearAll}
            disabled={!hasSelectedFilters}
          >
            Clear all
          </TextButton>
        </div>
      )}
      {/* Keyed on the set with options: Accordion.Multi captures initialOpenKeys once at mount, so a
          filter that appears after a search or category change would otherwise render collapsed. */}
      <Accordion.Multi
        key={accordionItems.map((item) => item.key).join(",")}
        items={accordionItems}
        initialOpenKeys={accordionItems.map((item) => item.key)}
        classNames={{ header: styles.accordionHeader, content: styles.accordionContent }}
      />
    </div>
  );
};

/** The panel dressed for `FilterLayout`'s column. */
const FilterRail = <TSearch, TFilterOptions>({
  showFilterPanel,
  filterOptions,
  definitions,
  filters,
  onChange,
  hasSelectedFilters,
  clearAll,
}: FilterPanelState<TSearch, TFilterOptions>) => {
  if (!showFilterPanel || !filterOptions) {
    return null;
  }
  return (
    <FilterPanel<TSearch, TFilterOptions>
      definitions={definitions}
      filterOptions={filterOptions}
      filters={filters}
      onChange={onChange}
      hasSelectedFilters={hasSelectedFilters}
      onClearAll={clearAll}
    />
  );
};

/** The removable chips above the grid, shown only alongside the panel. */
const AppliedFilterRow = <TSearch, TFilterOptions>({
  showFilterPanel,
  filterOptions,
  definitions,
  filters,
  onChange,
  hasSelectedFilters,
  clearAll,
}: FilterPanelState<TSearch, TFilterOptions>) => {
  if (!showFilterPanel || !filterOptions) {
    return null;
  }
  return (
    <AppliedFilters hasSelectedFilters={hasSelectedFilters} onClearAll={clearAll}>
      <AppliedFilterTags<TSearch, TFilterOptions>
        definitions={definitions}
        options={filterOptions}
        value={filters}
        onChange={onChange}
        variant="accent"
      />
    </AppliedFilters>
  );
};

export default Object.assign(FilterPanel, { Rail: FilterRail, AppliedRow: AppliedFilterRow });
